import type { AlertRuleType } from "../types";

/**
 * Almikia Epic ER (TASK-007): the shared, pure sentence builder behind every
 * lead-alert surface (this ticket's "Quién te necesita hoy" alert cards and
 * lead-page pills, and TASK-008's Dirección view). Turns a raised alert into
 * ONE plain-Spanish line about the LEAD -- never the advisor -- following the
 * Independence guide (design-system.md rule 7: direct language, short
 * phrases, no exclamation marks, no jargon) and the same banned-term list
 * every other Almikia copy respects (empleado, jefe, horario, turno, guardia,
 * asistencia, puntualidad, sanción, apercibimiento, falta, obligatorio,
 * ranking -- none of the 4 sentences below uses any of them). No I/O: every
 * input is already-resolved data, so this is unit-testable without a render.
 */

export interface AlertMessageInput {
  ruleType: AlertRuleType;
  /** `lead_alerts.raised_at` -- when the evaluation engine last (re)raised the alert. */
  raisedAt: string;
  now: Date;
  /**
   * The rule's own qualifying threshold in hours -- `sin_seguimiento` /
   * `alta_intencion_sin_actividad` read `alert_rules.hours_threshold`;
   * `detenido_en_etapa` reads the CURRENT stage's own `target_hours`
   * (`deal_stage_configs`, surfaced as `dealStages[].targetHours`). `raised_at`
   * only records WHEN THE ENGINE LAST EVALUATED true, up to its ~60s throttle
   * window after the lead actually crossed the threshold (`evaluate_lead_alerts()`,
   * `supabase/schemas/02_functions.sql`) -- so the lead's TRUE elapsed staleness is
   * `thresholdHours + (now - raisedAt)`, never plain `now - raisedAt` (a lead
   * unopened for 50 h would otherwise still show "1 h" the moment Hoy loads).
   * Omitted (treated as 0) for a rule type with no comparable threshold
   * (`cumple_criterio_sin_cita`, whose sentence never renders an elapsed duration).
   */
  thresholdHours?: number;
  /**
   * `detenido_en_etapa`: the lead's CURRENT stage label (`deals.stage`
   * resolved through `dealStages`). Falls back to generic copy when unset
   * (a stage value with no configured label, defensive only).
   */
  currentStageLabel?: string;
  /**
   * `cumple_criterio_sin_cita`: the rule's configured `target_stage_key`
   * label (e.g. "Showing"). Falls back to generic copy when unset.
   */
  targetStageLabel?: string;
}

/**
 * The lead's true elapsed staleness: `thresholdHours` (when the lead crossed the
 * qualifying line) plus however long it has been since the engine raised the
 * alert for it. Never negative (defensive against clock skew).
 */
function elapsedHours(raisedAt: string, now: Date, thresholdHours = 0): number {
  const hoursSinceRaised =
    (now.getTime() - new Date(raisedAt).getTime()) / 3_600_000;
  return Math.max(0, thresholdHours + hoursSinceRaised);
}

/**
 * "5 h" under a day, "1 día"/"2 días" from a full day on -- acceptance
 * criterion 1's own examples. Whole hours/days only: an alert's elapsed time
 * is a plain-Spanish approximation, never a precise duration.
 */
export function humanizeElapsed(hours: number): string {
  if (hours < 24) return `${Math.max(1, Math.round(hours))} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "día" : "días"}`;
}

/**
 * Turns one raised alert into its one-line Spanish sentence. Returns an
 * empty string for a rule type this builder doesn't (yet) know -- callers
 * skip rendering that case rather than showing a blank card (defensive:
 * `ALERT_RULE_TYPES` is a closed catalogue today, but this keeps a future
 * 5th type from crashing the UI before its own sentence branch lands).
 */
export function buildAlertMessage(input: AlertMessageInput): string {
  const elapsed = humanizeElapsed(
    elapsedHours(input.raisedAt, input.now, input.thresholdHours),
  );

  switch (input.ruleType) {
    case "sin_seguimiento":
      return `Lleva ${elapsed} sin seguimiento`;
    case "alta_intencion_sin_actividad":
      return `Tiene alta intención y lleva ${elapsed} sin actividad`;
    case "detenido_en_etapa":
      return input.currentStageLabel
        ? `Lleva ${elapsed} detenido en ${input.currentStageLabel}`
        : `Lleva ${elapsed} detenido en esta etapa`;
    case "cumple_criterio_sin_cita":
      return input.targetStageLabel
        ? `Ya cumple para ${input.targetStageLabel} y aún no tiene cita`
        : `Ya cumple para agendar y aún no tiene cita`;
    default:
      return "";
  }
}
